let onlineHandler = null;
let offlineHandler = null;
let visibilityHandler = null;
let focusHandler = null;
let reprobeIntervalId = null;
let activeRef = null;
let activeProbeUrl = null;

const COLD_START_TIMEOUT_MS = 15000;
const STEADY_TIMEOUT_MS = 8000;
const INITIAL_RETRY_DELAYS_MS = [0, 1000, 2500];
const REPROBE_INTERVAL_MS = 30000;

async function probeOnce(probeUrl, timeoutMs) {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), timeoutMs);
    try {
        const response = await fetch(probeUrl || './', {
            method: 'HEAD',
            cache: 'no-store',
            signal: controller.signal,
            headers: {
                'X-Api-Version': '1.0'
            }
        });
        // 429 means the liveness endpoint is rate-limiting us (common behind a shared NAT IP), not
        // that the backend is down — treat it as reachable so the app doesn't flap to offline.
        if (probeUrl) return response.ok || response.status === 429;
        return response.ok || response.status === 404 || response.status === 405;
    } catch {
        return false;
    } finally {
        clearTimeout(timeoutId);
    }
}

// Resilient initial probe: a slow or transient cold start must not be mistaken for offline,
// so retry with backoff and a forgiving timeout before concluding the server is unreachable.
// A genuine browser-offline state short-circuits to offline without waiting on retries.
async function probeWithRetries(probeUrl) {
    if (!navigator.onLine) return false;
    for (let attempt = 0; attempt < INITIAL_RETRY_DELAYS_MS.length; attempt++) {
        if (attempt > 0) {
            await new Promise(resolve => setTimeout(resolve, INITIAL_RETRY_DELAYS_MS[attempt]));
            if (!navigator.onLine) return false;
        }
        if (await probeOnce(probeUrl, COLD_START_TIMEOUT_MS)) {
            return true;
        }
    }
    return false;
}

function report(isOnline) {
    return activeRef ? activeRef.invokeMethodAsync('UpdateStatus', isOnline) : Promise.resolve();
}

// Self-healing re-probe driving the shared service-health state in both directions: it recovers a
// stuck false-offline AND flips the app to offline when the backend process stops responding while the
// browser network stays up. Skipped-to-offline while the browser reports offline so
// we don't spend a timeout on a request that cannot possibly succeed.
async function reprobe() {
    if (!navigator.onLine) {
        report(false);
        return;
    }
    if (await probeOnce(activeProbeUrl, STEADY_TIMEOUT_MS)) {
        report(true);
        return;
    }
    // Confirm a failure before declaring the whole app offline: a single transient blip (one slow
    // response, a rolling restart, a momentary 503) must not flip everyone offline. Only report offline
    // when a second probe also fails.
    await new Promise(resolve => setTimeout(resolve, 1000));
    if (!navigator.onLine) {
        report(false);
        return;
    }
    report(await probeOnce(activeProbeUrl, STEADY_TIMEOUT_MS));
}

export const retryOnlineStatusProbe = async function () {
    await reprobe();
};

export const addOnlineStatusListener = async function (dotNetRef, probeUrl) {
    activeRef = dotNetRef;
    activeProbeUrl = probeUrl;

    async function onOnline() {
        const isOnline = await probeOnce(probeUrl, STEADY_TIMEOUT_MS);
        report(isOnline);
    }

    function onOffline() {
        report(false);
    }

    async function onVisibility() {
        if (document.visibilityState === 'visible') {
            await reprobe();
        }
    }

    async function onFocus() {
        await reprobe();
    }

    onlineHandler = onOnline;
    offlineHandler = onOffline;
    visibilityHandler = onVisibility;
    focusHandler = onFocus;

    window.addEventListener('online', onlineHandler);
    window.addEventListener('offline', offlineHandler);
    document.addEventListener('visibilitychange', visibilityHandler);
    window.addEventListener('focus', focusHandler);

    reprobeIntervalId = setInterval(reprobe, REPROBE_INTERVAL_MS);

    // A genuine browser-offline state is known instantly, so conclude it before returning: startup
    // awaits this call, and the offline classification must be settled by the time ClassifyAsync runs
    // (otherwise an offline client is mistakenly probed and classed as a server outage).
    if (!navigator.onLine) {
        // Swallow a failed status report so it never faults the startup InitializeAsync that awaits
        // this call; a missed report degrades to the Checking state rather than crashing the app.
        await report(false).catch(() => { });
        return;
    }

    // The browser reports online but the server may be slow to cold-start. Run the resilient probe
    // WITHOUT blocking: awaiting its full retry budget here would freeze the UI on the loading splash
    // for the whole window. The result arrives asynchronously via UpdateStatus; State stays Checking
    // until then, and ClassifyAsync does its own short-timeout probes in the meantime.
    probeWithRetries(probeUrl).then(report);
};

export const removeOnlineStatusListener = function () {
    if (onlineHandler) {
        window.removeEventListener('online', onlineHandler);
        onlineHandler = null;
    }
    if (offlineHandler) {
        window.removeEventListener('offline', offlineHandler);
        offlineHandler = null;
    }
    if (visibilityHandler) {
        document.removeEventListener('visibilitychange', visibilityHandler);
        visibilityHandler = null;
    }
    if (focusHandler) {
        window.removeEventListener('focus', focusHandler);
        focusHandler = null;
    }
    if (reprobeIntervalId !== null) {
        clearInterval(reprobeIntervalId);
        reprobeIntervalId = null;
    }
    activeRef = null;
    activeProbeUrl = null;
};
